import { Component, Input, OnInit, OnChanges, SimpleChanges, inject, signal, computed, ViewChild, OnDestroy, DestroyRef, ElementRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { forkJoin, of } from 'rxjs';
import { map, catchError, filter, take } from 'rxjs/operators';

import { RapiderJsonViewerComponent } from '@rapider/components/json-viewer';
import { RapiderListGridComponent } from '@rapider/list-grid';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';

import { ProjectModelControllerService } from '@rapider/sdk';
import { ProjectDataService, selectProjectModelsList } from '@rapider/project-data';
import { NotificationService } from '@rapider/services';

// Import the new Spreadsheet Library
import { SpreadsheetViewerComponent, EntityFieldDef, CellUpdateEvent, FieldDataType } from '@rapider/spreadsheet';

import {
  DATA_INSIGHT_CONFIG,
  extractUiEntityConfig,
  buildFetchRecordsWithRelationsPath,
  generateTimelineGridConfig,
  getRelationDisplayType,
  buildUpdateDiffClassMap,
  getGridConfigForModel,
  getTargetModelTitle
} from '../database.config';
import { DataCardComponent } from '../data-card/data-card.component';
import { CdkAriaLive } from "../../../../../node_modules/@angular/cdk/types/_a11y-module-chunk";

/**
 * PURE FUNCTION: Converts a camelCase string into Title Case.
 * Example: "createdById" -> "Created By Id", "firstName" -> "First Name"
 * @param {string} text - The camelCase string to convert.
 * @returns {string} The Title Case formatted string.
 */
export function camelToTitleCase(text: string): string {
  if (!text) return '';
  const result = text.replace(/([A-Z])/g, ' $1');
  return result.charAt(0).toUpperCase() + result.slice(1).trim();
}

/**
 * PURE FUNCTION: Maps backend Project Model field definitions into strict Spreadsheet EntityFieldDefs.
 * Extracts explicit titles from the UI Config metadata, or falls back to Title Case.
 * @param {any[]} fields - The raw fields from the project model.
 * @param {any} uiConfig - The parsed uiEntityConfig metadata from the project model.
 * @returns {any[]} Validated configuration array for the spreadsheet viewer.
 */
export function mapProjectModelFieldsToSpreadsheetFields(fields: any[], uiConfig?: any): any[] {
  if (!fields || !Array.isArray(fields)) return [];

  // Extract explicitly configured columns from UI metadata
  const configuredColumns = uiConfig?.config?.listGridConfig?.columns || [];

  // CRITICAL FIX: Hide system relation ID fields completely from the UI per architectural rules
  const excludedFields = ['createdById', 'updatedById', 'deletedById', 'deletedDate', 'deletedBy', 'isDeleted'];
  const auditFields = ['createdDate', 'createdBy', 'updatedDate', 'updatedBy'];
  const idFields = ['id', '_id'];

  // Date Types specifically requested
  const dateTypes = [
    'date',
    'geopoint',
    'datestring',
    'datetime',
    'timestamp',
    'timestamp_without_timezone',
    'timestamp_with_timezone'
  ];

  return fields
    .filter(field => !excludedFields.includes(field.name))
    .map(field => {
      // Find if this field has a custom configured title in the UI metadata
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const colConfig = configuredColumns.find((c: any) => c.fieldName === field.name);

      // Resolve the display title: configured title > fallback to Title Case of the field name
      const displayTitle = colConfig?.title || camelToTitleCase(field.name);

      // Map backend type strings to valid Spreadsheet FieldDataTypes
      let mappedType = 'string';
      const rawType = (field.type || '').toLowerCase();

      if (dateTypes.includes(rawType)) {
        mappedType = 'date';
      } else if (rawType.includes('number') || rawType.includes('int') || rawType.includes('float')) {
        mappedType = 'number';
      } else if (rawType.includes('bool')) {
        mappedType = 'boolean';
      } else if (field.enumValues && field.enumValues.length > 0) {
        mappedType = 'enum';
      }

      // Determine if field should be initially hidden (Audit or ID)
      const isId = idFields.includes(field.name);
      const isAudit = auditFields.includes(field.name);

      return {
        key: field.name,
        title: displayTitle,
        type: mappedType as FieldDataType,
        format: field.format, // Pass format config mapping to allow MomentJS formatting
        required: field.isRequired,
        enumValues: field.enumValues,
        readOnly: field.isPrimaryKey === true || field.isAutoGenerated === true || field.isId === true || field.isMixinField === true,
        width: 150, // Default width
        hiddenByDefault: isId || isAudit // Custom agnostic flag to inform the spreadsheet engine
      };
    });
}

/**
 * @class ProjectModelSpreadsheetViewerComponent
 * @description A generic component that fetches project model records and displays them in an editable Univer spreadsheet.
 */
@Component({
  selector: 'rapider-project-model-spreadsheet-viewer',
  standalone: true,
  imports: [
    CommonModule,
    SpreadsheetViewerComponent, // Replaced ListGrid with Spreadsheet
    RapiderListGridComponent, // Kept for relation fallback grids
    RapiderJsonViewerComponent,
    NzTagModule,
    NzDividerModule,
    DataCardComponent,
    NzPaginationModule,
    NzButtonModule,
    NzPopconfirmModule
  ],
  templateUrl: './project-model-spreadsheet-viewer.component.html'
})
export class ProjectModelSpreadsheetViewerComponent implements OnInit, OnChanges, OnDestroy {
  // Capture the child component to command row highlighting
  @ViewChild(SpreadsheetViewerComponent) spreadsheetViewer!: SpreadsheetViewerComponent;

  private readonly store = inject(Store);
  private readonly projectDataService = inject(ProjectDataService);
  private readonly notificationService = inject(NotificationService);
  private readonly projectModelController = inject(ProjectModelControllerService);
  private readonly el = inject(ElementRef);

  // DOM dependencies and Observer for theme tracking
  // private readonly document = inject(DOCUMENT);
  private themeObserver: MutationObserver | null = null;
  private readonly destroyRef = inject(DestroyRef);
  private lastNewRecordWarning: number = 0; // Debounce tracking for new record notifications

  @Input({ required: true }) projectModelName!: string;
  @Input() recordIds?: string[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() payloadData: any = null;
  @Input() operationType: string = 'READ';

  // CRITICAL FIX: Accepts a dummy counter from the parent to force a hard reload of data
  @Input() refreshTrigger: number = 0;

  @Input() displayPagination = false

  // CRITICAL FIX: Accepts the fully hydrated model directly from the parent to prevent cold-start API 404s
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() explicitModel: any = null;

  public config = DATA_INSIGHT_CONFIG;
  public getRelationDisplayType = getRelationDisplayType;
  public generateTimelineGridConfig = generateTimelineGridConfig;
  public getGridConfigForModel = getGridConfigForModel;
  public getTargetModelTitle = getTargetModelTitle;

  public isLoading = signal<boolean>(false);
  public isRelationsLoading = signal<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public data = signal<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public selectedRecord = signal<any | null>(null);

  // CRITICAL FIX: Local cache to save the model on hard reloads when NgRx store is empty
  public fetchedModel = signal<any>(null);

  public cachedModels = computed(() => this.store.selectSignal(selectProjectModelsList)() || []);

  // Pagination State Signals
  public currentPage = signal<number>(1);
  public pageSize = computed<number>(() => {
    return this.uiConfig()?.config?.listGridConfig?.pagination?.pageSize || 40;
  });
  public totalItems = signal<number>(0);

  // Unsaved Changes Tracking Signals
  public pendingChanges = signal<Map<string, any>>(new Map());
  public isSaving = signal<boolean>(false);

  // CRITICAL FIX: Signals to track custom toolbar toggle states
  public isIdVisible = signal<boolean>(false);
  public isAuditVisible = signal<boolean>(false);

  // Theme Tracking Signal
  // NOTE: If you have a global NgRx Store or ThemeService that tracks the footer's dark mode clicks, 
  // you should update this signal inside your ngOnInit!
  public isAppInDarkMode = signal<boolean>(false);

  // Local text config
  public localTexts = {
    specificRecordsInfo: $localize`:@@dataViewer.specificRecordsInfo:Showing results for specific records`,
    noData: $localize`:@@dataViewer.noDataSpreadsheet:No data available. Please enter data directly into the spreadsheet to create new records.`,
    unsavedChanges: $localize`:@@dataViewer.unsavedChanges:unsaved row(s).`,
    saveChangesBtn: $localize`:@@dataViewer.saveChangesBtn:Save Changes`,
    discardChangesBtn: $localize`:@@dataViewer.discardChangesBtn:Discard Changes`,
    discardConfirm: $localize`:@@dataViewer.discardConfirm:Are you sure you want to discard the changes?`,
    batchSuccessTitle: $localize`:@@dataViewer.batchSuccessTitle:Saved Successfully`,
    batchSuccessDesc: $localize`:@@dataViewer.batchSuccessDesc:All changes have been saved to the database.`,
    batchErrorTitle: $localize`:@@dataViewer.batchErrorTitle:Partial Success`,
    batchErrorDesc: $localize`:@@dataViewer.batchErrorDesc:Some rows failed to save. Please review the rows highlighted in red.`,
    invalidRowsSkipped: $localize`:@@dataViewer.invalidRowsSkipped:Some rows have invalid input and were not saved. You can find these errors highlighted in red, please fix the errors and try again.`
  };

  /**
     * PURE FUNCTION: Constructs the API path to fetch paginated "All Data".
     * Includes a safe fallback path resolution if the base model hasn't completely hydrated yet.
     */
  private buildFindAllPath(modelName: string, skip: number, limit: number): string {
    const basePath = this.model()?.basePath || `/${modelName.toLowerCase()}`;
    return `${basePath}?skip=${skip}&limit=${limit}`;
  }

  public model = computed(() => {
    // 1. CRITICAL FIX: Always trust the explicitly passed model from the parent first
    if (this.explicitModel) return this.explicitModel;

    // 2. If we successfully fetched the explicit model details via API fallback, use that
    const explicitModel = this.fetchedModel();
    if (explicitModel) return explicitModel;

    // 3. Otherwise search the global NgRx cache
    const models = this.cachedModels();
    if (!models) return null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return models.find((m: any) =>
      (m.name && m.name.toLowerCase() === this.projectModelName.toLowerCase()) ||
      (m.dataTableName && m.dataTableName.toLowerCase() === this.projectModelName.toLowerCase())
    ) || null;
  });

  public uiConfig = computed(() => extractUiEntityConfig(this.model()));

  public diffClassMap = computed(() => {
    if (this.operationType === 'UPDATE' && this.payloadData) {
      return buildUpdateDiffClassMap(this.payloadData);
    }
    return {};
  });

  // Dynamically map the backend fields to the strict Spreadsheet field definitions
  public spreadsheetFields = computed(() => {
    const currentModel = this.model();
    const currentUiConfig = this.uiConfig();
    return mapProjectModelFieldsToSpreadsheetFields(currentModel?.fields || [], currentUiConfig);
  });

  ngOnInit() {
    // 1. Initialize Dark Mode State
    const htmlElement = document.documentElement;
    this.isAppInDarkMode.set(htmlElement.classList.contains('dark'));

    // 2. Attach a MutationObserver to watch for footer theme toggles
    this.themeObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          this.isAppInDarkMode.set(htmlElement.classList.contains('dark'));
        }
      });
    });

    // Start observing the HTML tag for class attribute changes
    this.themeObserver.observe(htmlElement, { attributes: true });

    // CRITICAL FIX: Intercept Enter key in the capture phase to override default move-down behavior.
    // If not in edit mode, we prevent default and dispatch F2 to enter edit mode natively with the cursor at the end!
    this.el.nativeElement.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        const target = event.target as HTMLElement;
        // Detect if Univer is already in edit mode (textarea or contenteditable is active)
        if (target.tagName.toLowerCase() === 'textarea' || target.tagName.toLowerCase() === 'input' || target.isContentEditable) {
          return; // Let native Enter happen (save & move down)
        }

        event.preventDefault();
        event.stopPropagation();

        const f2Event = new KeyboardEvent('keydown', {
          key: 'F2',
          code: 'F2',
          keyCode: 113,
          which: 113,
          bubbles: true,
          cancelable: true
        });
        target.dispatchEvent(f2Event);
      }
    }, true);

    // CRITICAL FIX: Since the parent component now passes the fully hydrated `explicitModel` directly,
    // we no longer need to wait for the global NgRx store to hydrate. 
    // We can safely fetch immediately without hitting a 404!
    this.fetchRelationsAndData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if ((changes['projectModelName'] && !changes['projectModelName'].isFirstChange()) ||
      (changes['recordIds'] && !changes['recordIds'].isFirstChange()) ||
      (changes['refreshTrigger'] && !changes['refreshTrigger'].isFirstChange())) {

      this.currentPage.set(1);

      // CRITICAL FIX: Clear pending changes immediately so user doesn't get blocked by the warning
      if (changes['refreshTrigger']) {
        this.pendingChanges.set(new Map());
      }

      this.fetchRelationsAndData();
    }
  }

  private fetchRelationsAndData(): void {
    const currentModel = this.model();

    if (!currentModel || !currentModel.id) {
      this.isLoading.set(true);
      this.projectDataService.getProjectModelWithoutRelations(this.projectModelName).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (res: any) => {
          // Robust parser: Handles single objects, array responses, or paginated DTOs
          let modelObj = null;
          if (Array.isArray(res) && res.length > 0) modelObj = res[0];
          else if (res && res.items && res.items.length > 0) modelObj = res.items[0];
          else if (res && res.id) modelObj = res;

          if (modelObj && modelObj.id) {
            this.fetchedModel.set(modelObj); // Cache locally so path builder doesn't crash!
            this.fetchFullModelAndRelations(modelObj.id);
          } else {
            this.fetchData([]);
          }
        },
        error: () => this.fetchData([])
      });
      return;
    }

    if (currentModel.relations && Array.isArray(currentModel.relations)) {
      this.fetchData(currentModel.relations);
    } else {
      this.fetchFullModelAndRelations(currentModel.id);
    }
  }

  private fetchFullModelAndRelations(modelId: string): void {
    this.isRelationsLoading.set(true);
    this.projectDataService.getProjectModelCompleteDetail(modelId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (completeModel) => {
        this.fetchedModel.set(completeModel); // Cache locally so path builder doesn't crash!
        this.isRelationsLoading.set(false);
        this.fetchData(completeModel.relations || []);
      },
      error: (err) => {
        console.error('Failed to fetch model and relations:', err);
        this.isRelationsLoading.set(false);
        this.fetchData([]);
      }
    });
  }

  private fetchData(relations: any[]): void {
    if (!this.projectModelName) {
      this.data.set([]);
      this.selectedRecord.set(null);
      this.isLoading.set(false);
      return;
    }

    this.isLoading.set(true);
    const isSpecificRecordsMode = this.recordIds && this.recordIds.length > 0;

    if (isSpecificRecordsMode) {
      const path = buildFetchRecordsWithRelationsPath(this.projectModelName, this.recordIds!, relations);
      this.projectDataService.executeProjectApiCall('GET', path, null).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (response) => {
          const arr = Array.isArray(response) ? response : (response ? [response] : []);
          const sortedArr: any[] = [];
          const idMap = new Map();
          arr.forEach(item => {
            const id = item.id || item._id;
            if (id) idMap.set(id.toString(), item);
          });

          this.recordIds!.forEach(id => {
            const key = id.toString();
            if (idMap.has(key)) {
              sortedArr.push(idMap.get(key));
              idMap.delete(key);
            }
          });

          idMap.forEach(item => sortedArr.push(item));
          this.data.set(sortedArr);
          this.totalItems.set(sortedArr.length);
          this.isLoading.set(false);
        },
        error: () => {
          this.data.set([]);
          this.isLoading.set(false);
        }
      });
    } else {
      const limit = this.pageSize();
      const skip = (this.currentPage() - 1) * limit;
      const path = this.buildFindAllPath(this.projectModelName, skip, limit);

      this.projectDataService.executeProjectApiCall('GET', path, null).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (response: any) => {
          let records: any[] = [];
          let totalCount = 0;

          if (Array.isArray(response)) {
            records = response;
            totalCount = response.length;
          } else if (response && response.items) {
            records = response.items;
            totalCount = response.total || response.count || records.length;
          } else if (response) {
            records = [response];
            totalCount = 1;
          }

          this.data.set(records);
          this.totalItems.set(totalCount);
          this.isLoading.set(false);
        },
        error: () => {
          this.data.set([]);
          this.isLoading.set(false);
        }
      });
    }
  }

  public onPageChange(page: number): void {
    // Prevent navigation if there are unsaved changes
    if (this.pendingChanges().size > 0) {
      this.notificationService.createNotification('warning', 'Unsaved Changes', 'Please save your changes before navigating pages.');
      return;
    }
    this.currentPage.set(page);
    this.fetchRelationsAndData();
  }

  /**
   * Toggles the visibility of ID fields in the spreadsheet viewer.
   */
  public toggleIdFields(): void {
    const newState = !this.isIdVisible();
    this.isIdVisible.set(newState);
    const idFields = ['id', '_id'];
    const indices = this.spreadsheetFields().map((f, i) => idFields.includes(f.key) ? i : -1).filter(i => i !== -1);
    if (this.spreadsheetViewer) {
      this.spreadsheetViewer.toggleColumnsVisibility(indices, newState);
    }
  }

  /**
   * Toggles the visibility of Audit fields in the spreadsheet viewer.
   */
  public toggleAuditFields(): void {
    const newState = !this.isAuditVisible();
    this.isAuditVisible.set(newState);
    const auditFields = ['createdDate', 'createdBy', 'updatedDate', 'updatedBy', 'deletedDate', 'deletedBy'];
    const indices = this.spreadsheetFields().map((f, i) => auditFields.includes(f.key) ? i : -1).filter(i => i !== -1);
    if (this.spreadsheetViewer) {
      this.spreadsheetViewer.toggleColumnsVisibility(indices, newState);
    }
  }

  /**
      * Action triggered when the Univer spreadsheet successfully validates an edit.
      * Caches the change locally and highlights the row instead of immediately patching.
      * @param {CellUpdateEvent} event - The payload from the spreadsheet component.
      */
  public handleCellUpdate(event: CellUpdateEvent): void {
    const currentMap = new Map(this.pendingChanges());
    const rowChanges = currentMap.get(event.rowId) || {};

    // 1. Handle value reverting (User typed the original value back, or pasted identical data)
    if (event.isRevertedToOriginal) {
      delete rowChanges[event.fieldKey];

      const invalidSet = new Set(rowChanges['_invalidFields'] || []);
      invalidSet.delete(event.fieldKey);
      rowChanges['_invalidFields'] = Array.from(invalidSet);

      const remainingChangedKeys = Object.keys(rowChanges).filter(k => !k.startsWith('_'));

      if (remainingChangedKeys.length === 0 && !event.isNewRecord) {
        // The row is completely reverted to its original backend state! Remove entirely.
        currentMap.delete(event.rowId);
        this.pendingChanges.set(currentMap);
        this.spreadsheetViewer?.setRowHighlight(event.rowId, 'clear');
        return;
      }
    } else {
      // 2. Append the newly changed field
      rowChanges[event.fieldKey] = event.newValue;

      const invalidSet = new Set(rowChanges['_invalidFields'] || []);
      if (event.isValid === false) {
        invalidSet.add(event.fieldKey);
      } else {
        invalidSet.delete(event.fieldKey);
      }
      rowChanges['_invalidFields'] = Array.from(invalidSet);
    }

    if (event.isNewRecord) {
      rowChanges['_isNewRecord'] = true;
      rowChanges['_rowIndex'] = event.rowIndex;
    }

    currentMap.set(event.rowId, rowChanges);
    this.pendingChanges.set(currentMap);

    // 3. Visually highlight the row and specific target cells based on new aggregate state
    if (this.spreadsheetViewer) {
      const isNew = event.isNewRecord || rowChanges['_isNewRecord'];
      const changedKeys = Object.keys(rowChanges).filter(k => !k.startsWith('_'));
      const explicitErrors = rowChanges['_invalidFields'] as string[];

      if (isNew) {
        // CRITICAL FIX: Exclude readOnly fields entirely from required validation checks
        const excludedRequired = ['id', '_id', 'createdDate', 'createdBy', 'updatedDate', 'updatedBy', 'deletedDate', 'deletedBy'];
        const requiredFields = this.spreadsheetFields().filter(f => f.required && !f.readOnly && !excludedRequired.includes(f.key));

        const missingFields = requiredFields.filter(f => {
          const val = rowChanges[f.key];
          return val === undefined || val === null || val === '';
        }).map(f => f.key);

        const allErrors = [...explicitErrors, ...missingFields];

        if (allErrors.length > 0) {
          // CRITICAL FIX: Pass an empty array for changedKeys so individual modified cells in a NEW row don't turn yellow
          this.spreadsheetViewer.setRowHighlight(event.rowId, 'new_error', [], allErrors);

          const now = Date.now();
          if (now - this.lastNewRecordWarning > 3000) {
            this.notificationService.createNotification('warning', 'Missing Required Fields', 'Please fill all required fields to save the new record.');
            this.lastNewRecordWarning = now;
          }
        } else {
          // CRITICAL FIX: Pass an empty array for changedKeys so individual modified cells in a NEW row don't turn yellow
          this.spreadsheetViewer.setRowHighlight(event.rowId, 'new_ready', [], allErrors);
        }
      } else {
        if (explicitErrors.length > 0) {
          this.spreadsheetViewer.setRowHighlight(event.rowId, 'error', changedKeys, explicitErrors);
        } else {
          this.spreadsheetViewer.setRowHighlight(event.rowId, 'changed', changedKeys, []);
        }
      }
    }
  }

  /**
    * Handles row selection emitted by the Univer spreadsheet to show the data card.
    * @param {any} record - The selected row data.
    */
  public handleRowSelected(record: any): void {
    this.selectedRecord.set(record);
  }

  /**
   * Reverts all pending changes back to their original values natively in the spreadsheet
   * without making a full API network reload.
   */
  public discardChanges(): void {
    if (this.spreadsheetViewer && this.pendingChanges().size > 0) {
      this.spreadsheetViewer.revertChanges(this.pendingChanges());
    }
    this.pendingChanges.set(new Map());
  }

  /**
   * Executes bulk PATCH requests for all locally cached changes.
   * Operates in parallel and handles individual row success/failure highlighting.
   */
  public saveChanges(): void {
    const currentModel = this.model();
    if (!currentModel || !currentModel.basePath) return;

    const changes = Array.from(this.pendingChanges().entries());
    if (changes.length === 0) return;

    let hasValidationErrors = false;
    const validChanges = [];

    // Pre-flight Validation Check
    for (const [rowId, partialData] of changes) {
      const invalidFields = partialData['_invalidFields'] || [];
      const isNew = partialData['_isNewRecord'];

      const excludedRequired = ['id', '_id', 'createdDate', 'createdBy', 'updatedDate', 'updatedBy', 'deletedDate', 'deletedBy'];
      let missingFields: string[] = [];

      if (isNew) {
        const requiredFields = this.spreadsheetFields().filter(f => f.required && !excludedRequired.includes(f.key));
        missingFields = requiredFields.filter(f => {
          const val = partialData[f.key];
          return val === undefined || val === null || val === '';
        }).map(f => f.key);
      }

      if (invalidFields.length > 0 || missingFields.length > 0) {
        hasValidationErrors = true;
        continue; // Skip this row because it contains invalid inputs or missing required data
      }

      validChanges.push({ rowId, partialData });
    }

    if (validChanges.length === 0) {
      if (hasValidationErrors) {
        this.notificationService.createNotification('warning', 'Validation Errors', this.localTexts.invalidRowsSkipped);
      }
      return;
    }

    this.isSaving.set(true);

    // Prepare an array of isolated observable requests for valid rows
    const updateRequests = validChanges.map(({ rowId, partialData }) => {
      const isNew = partialData['_isNewRecord'];
      const cleanData = { ...partialData };
      delete cleanData['_isNewRecord'];
      delete cleanData['_rowIndex'];
      delete cleanData['_invalidFields'];

      if (isNew) {
        return this.projectDataService.executeProjectApiCall('POST', currentModel.basePath, cleanData).pipe(
          map(() => ({ rowId, success: true, isNew: true })),
          catchError((err) => of({ rowId, success: false, error: err, isNew: true }))
        );
      } else {
        return this.projectDataService.updateProjectModelDataById(currentModel.basePath, rowId, cleanData).pipe(
          map(() => ({ rowId, success: true, isNew: false })),
          catchError((err) => of({ rowId, success: false, error: err, isNew: false }))
        );
      }
    });

    // Execute all patches concurrently
    forkJoin(updateRequests).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((results) => {
      this.isSaving.set(false);
      const newPendingMap = new Map(this.pendingChanges());
      let hasErrors = false;
      let hasNewRecords = false;

      results.forEach(res => {
        if (res.success) {
          // Remove from queue and clear highlight
          newPendingMap.delete(res.rowId);
          this.spreadsheetViewer?.setRowHighlight(res.rowId, 'clear');

          if (res.isNew) {
            hasNewRecords = true;
          } else {
            // Silently update local data array
            const currentData = this.data();
            const rowIndex = currentData.findIndex(item => (item.id || item._id) === res.rowId);
            if (rowIndex > -1) {
              const pendingData = this.pendingChanges().get(res.rowId);
              if (pendingData) {
                const cleanedUpdate = { ...pendingData };
                delete cleanedUpdate['_invalidFields'];
                Object.assign(currentData[rowIndex], cleanedUpdate);
              }
            }
          }
        } else {
          // Keep in queue and highlight red
          hasErrors = true;
          this.spreadsheetViewer?.setRowHighlight(res.rowId, res.isNew ? 'new_error' : 'error');
        }
      });

      this.pendingChanges.set(newPendingMap);

      if (hasErrors) {
        this.notificationService.createNotification('warning', this.localTexts.batchErrorTitle, this.localTexts.batchErrorDesc);
      } else if (hasValidationErrors) {
        // Some rows succeeded, but others were skipped entirely
        this.notificationService.createNotification('warning', 'Partial Save', this.localTexts.invalidRowsSkipped);
      } else {
        this.notificationService.createNotification('success', this.localTexts.batchSuccessTitle, this.localTexts.batchSuccessDesc);
      }

      // If new records were created successfully, reload the grid to get proper IDs from the backend
      if (hasNewRecords) {
        this.fetchRelationsAndData();
      }
    });
  }

  /**
   * PURE FUNCTION: Safely wraps relation data into an array so it can be passed to grid visualizers properly.
   * @param {any} data - Raw relation data (can be an object or an array).
   * @returns {any[]} An array format of the data.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public normalizeDataAsArray(data: any): any[] {
    if (!data) return [];
    return Array.isArray(data) ? data : [data];
  }

  /**
   * Cleans up local maps and disconnects DOM observers when the component is destroyed.
   */
  ngOnDestroy(): void {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
    this.pendingChanges.set(new Map());
  }

}